/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

/* eslint-disable max-classes-per-file */
// Splitting classes across files runs into issues with circular dependencies
// and makes it harder to understand the code structure.

import { children, firstItem, singleItems } from './utils';
import { Builder } from '../builder';
import { isProperNode } from '../ast/helpers';
const isRenameExpression = (parent, node) => {
  return parent.type === 'command' && parent.name === 'rename' && node.type === 'option' && node.name === 'as';
};
export class VisitorContext {
  constructor(
  /**
   * Global visitor context.
   */
  ctx,
  /**
   * ES|QL AST node which is currently being visited.
   */
  node,
  /**
   * Context of the parent node, from which the current node was reached
   * during the AST traversal.
   */
  parent = null) {
    this.ctx = ctx;
    this.node = node;
    this.parent = parent;
  }
  *visitArguments(input) {
    this.ctx.assertMethodExists('visitExpression');
    for (const arg of this.arguments()) {
      if (!arg) continue;
      if (arg.type === 'option' && !isRenameExpression(this.node, arg)) {
        continue;
      }
      yield this.visitExpression(arg, typeof input === 'function' ? input() : input);
    }
  }
  arguments() {
    const node = this.node;
    if (!isProperNode(node)) {
      return [];
    }
    const args = [];
    for (const arg of children(node)) {
      args.push(arg);
    }
    return args;
  }
  visitArgument(index, input) {
    this.ctx.assertMethodExists('visitExpression');
    const node = this.node;
    if (!isProperNode(node)) {
      throw new Error('Node does not have arguments');
    }
    let i = 0;
    for (const arg of this.arguments()) {
      if (i === index) {
        return this.visitExpression(arg, input);
      }
      i++;
    }
    throw new Error(`Argument at index ${index} not found`);
  }
  visitExpression(expressionNode, input) {
    return this.ctx.visitExpression(this, expressionNode, input);
  }
  visitCommand(commandNode, input) {
    return this.ctx.visitCommand(this, commandNode, input);
  }
}
export class QueryVisitorContext extends VisitorContext {
  *commands() {
    yield* this.node.commands;
  }
  *visitCommands(input) {
    this.ctx.assertMethodExists('visitCommand');
    for (const cmd of this.node.commands) {
      yield this.visitCommand(cmd, input);
    }
  }
}

// Commands --------------------------------------------------------------------

export class CommandVisitorContext extends VisitorContext {
  name() {
    return this.node.name.toUpperCase();
  }
  *options() {
    for (const arg of this.node.args) {
      if (!arg || Array.isArray(arg)) {
        continue;
      }
      // We treat "AS" options as rename expressions, not as command options.
      if (isRenameExpression(this.node, arg)) continue;
      if (arg.type === 'option') {
        yield arg;
      }
    }
  }
  *visitOptions(input) {
    this.ctx.assertMethodExists('visitCommandOption');
    for (const option of this.options()) {
      const sourceContext = new CommandOptionVisitorContext(this.ctx, option, this);
      const result = this.ctx.methods.visitCommandOption(sourceContext, input);
      yield result;
    }
  }
  *args(option = '') {
    option = option.toLowerCase();
    if (!option) {
      for (const arg of this.node.args) {
        if (!arg) {
          continue;
        }
        if (Array.isArray(arg)) {
          yield arg;
          continue;
        }
        if (arg.type !== 'option') {
          yield arg;
        } else if (isRenameExpression(this.node, arg)) {
          // We treat "AS" options as rename expressions, not as command options.
          yield arg;
        }
      }
    }
    const optionNode = this.node.args.find(arg => !Array.isArray(arg) && arg && arg.type === 'option' && arg.name === option);
    if (optionNode) {
      yield* optionNode.args;
    }
  }
  *visitArgs(input, option = '') {
    this.ctx.assertMethodExists('visitExpression');
    for (const arg of singleItems(this.args(option))) {
      yield this.visitExpression(arg, typeof input === 'function' ? input() : input);
    }
  }
  *visitSources(input) {
    this.ctx.assertMethodExists('visitSourceExpression');
    for (const arg of singleItems(this.node.args)) {
      if (arg.type === 'source') {
        const sourceContext = new SourceExpressionVisitorContext(this.ctx, arg, this);
        const result = this.ctx.methods.visitSourceExpression(sourceContext, input);
        yield result;
      }
    }
  }
}
export class CommandOptionVisitorContext extends VisitorContext {}

// FROM <sources> [ METADATA <columns> ]
export class FromCommandVisitorContext extends CommandVisitorContext {
  /**
   * Visit the METADATA part of the FROM command.
   *
   *   FROM <sources> [ METADATA <columns> ]
   *
   * @param input Input object to pass to all "visitColumn" children methods.
   * @returns An iterable of results of all the "visitColumn" visitor methods.
   */
  *visitMetadataColumns(input) {
    this.ctx.assertMethodExists('visitColumnExpression');
    let metadataOption;
    for (const arg of singleItems(this.node.args)) {
      if (arg.type === 'option' && arg.name === 'metadata') {
        metadataOption = arg;
        break;
      }
    }
    if (!metadataOption) {
      return;
    }
    for (const arg of singleItems(metadataOption.args)) {
      if (arg.type === 'column') {
        const columnContext = new ColumnExpressionVisitorContext(this.ctx, arg, this);
        const result = this.ctx.methods.visitColumnExpression(columnContext, input);
        yield result;
      }
    }
  }
}

// LIMIT <literal>
export class LimitCommandVisitorContext extends CommandVisitorContext {
  /**
   * @returns The first numeric literal argument of the command.
   */
  numericLiteral() {
    const arg = firstItem(this.node.args);
    if (arg && arg.type === 'literal' && (arg.literalType === 'integer' || arg.literalType === 'double')) {
      return arg;
    }
  }

  /**
   * @returns The value of the first numeric literal argument of the command.
   */
  numeric() {
    const literal = this.numericLiteral();
    return literal === null || literal === void 0 ? void 0 : literal.value;
  }
  setLimit(value) {
    const literalNode = Builder.expression.literal.numeric({
      value,
      literalType: 'integer'
    });
    this.node.args = [literalNode];
  }
}

// EXPLAIN <query>
export class ExplainCommandVisitorContext extends CommandVisitorContext {}

// ROW <columns>
export class RowCommandVisitorContext extends CommandVisitorContext {}

// METRICS
export class MetricsCommandVisitorContext extends CommandVisitorContext {}

// SHOW <identifier>
export class ShowCommandVisitorContext extends CommandVisitorContext {}

// META <identifier>
export class MetaCommandVisitorContext extends CommandVisitorContext {}

// EVAL <columns>
export class EvalCommandVisitorContext extends CommandVisitorContext {}

// STATS <columns> [ BY <columns> ]
export class StatsCommandVisitorContext extends CommandVisitorContext {}

// INLINESTATS <columns> [ BY <columns> ]
export class InlineStatsCommandVisitorContext extends CommandVisitorContext {}

// LOOKUP <source> ON <column>
export class LookupCommandVisitorContext extends CommandVisitorContext {}

// KEEP <columns>
export class KeepCommandVisitorContext extends CommandVisitorContext {}

// SORT <columns>
export class SortCommandVisitorContext extends CommandVisitorContext {}

// WHERE <expression>
export class WhereCommandVisitorContext extends CommandVisitorContext {}

// DROP <columns>
export class DropCommandVisitorContext extends CommandVisitorContext {}

// RENAME <column> AS <column>
export class RenameCommandVisitorContext extends CommandVisitorContext {}

// DISSECT <column> <string> [ APPEND_SEPARATOR = <string> ]
export class DissectCommandVisitorContext extends CommandVisitorContext {}

// GROK <column> <string>
export class GrokCommandVisitorContext extends CommandVisitorContext {}

// ENRICH <column> [ ON <column> ] [ WITH <columns> ]
export class EnrichCommandVisitorContext extends CommandVisitorContext {}

// MV_EXPAND <column>
export class MvExpandCommandVisitorContext extends CommandVisitorContext {}

// <LOOKUP | LEFT | RIGHT> JOIN <target> ON <condition>
export class JoinCommandVisitorContext extends CommandVisitorContext {}

// CHANGE_POINT <value> [ ON <key> ] [ AS <targetType>, <targetPvalue> ]
export class ChangePointCommandVisitorContext extends CommandVisitorContext {}

// Expressions -----------------------------------------------------------------

export class ExpressionVisitorContext extends VisitorContext {}
export class ColumnExpressionVisitorContext extends VisitorContext {}
export class SourceExpressionVisitorContext extends VisitorContext {}
export class FunctionCallExpressionVisitorContext extends VisitorContext {
  /**
   * @returns Returns a printable uppercase function name or operator.
   */
  operator() {
    const operator = this.node.name;
    switch (operator) {
      case 'note_like':
        {
          return 'NOT LIKE';
        }
      case 'not_rlike':
        {
          return 'NOT RLIKE';
        }
    }
    return operator.toUpperCase();
  }
}
export class LiteralExpressionVisitorContext extends ExpressionVisitorContext {}
export class ListLiteralExpressionVisitorContext extends ExpressionVisitorContext {
  *visitElements(input) {
    this.ctx.assertMethodExists('visitExpression');
    for (const value of this.node.values) {
      yield this.visitExpression(value, typeof input === 'function' ? input() : input);
    }
  }
}
export class TimeIntervalLiteralExpressionVisitorContext extends ExpressionVisitorContext {}
export class InlineCastExpressionVisitorContext extends ExpressionVisitorContext {
  value() {
    this.ctx.assertMethodExists('visitExpression');
    const value = firstItem([this.node.value]);
    return value;
  }
  visitValue(input) {
    this.ctx.assertMethodExists('visitExpression');
    return this.visitExpression(this.value(), input);
  }
}
export class RenameExpressionVisitorContext extends VisitorContext {}
export class OrderExpressionVisitorContext extends VisitorContext {}
export class IdentifierExpressionVisitorContext extends VisitorContext {}